import * as React from 'react';
import {
  DetailsList,
  DetailsListLayoutMode,
  CheckboxVisibility,
  SelectionMode,
  Icon,
  ISelectableOption,
  Spinner,
  SpinnerSize,
} from '@fluentui/react';
import { useTranslation } from 'react-i18next';
import { LiquidacionInsumo } from '../../../../../system/settlement/aje/liquidacion-insumos';
import { DateTime } from 'luxon';
import TimeTaxSupportSystemClient from '../../../../../system';
import { TipoInsumos } from '../../../carga/enums/tipo_insumos.enum';
import {
  AgrupadorCorporacionLiquidadora,
  AgrupadorCorporacionLiquidadoraTipo,
} from '../../../../../system/agrupador_corporacion_liquidadora/AgrupadorCorporacionLiquidadoraSystem';
import { t } from 'i18next';
import { getStatusCircleClass } from '../../../carga/utils/settlementUtils';

const formatValida = (item: LiquidacionInsumo) => {
  if (item.valida === true) {
    return <Icon iconName="CheckMark" className="color-success" />;
  } else {
    return <Icon iconName="Cancel" className="color-error" />;
  }
};

const formatDescription = (item: LiquidacionInsumo) => {
  if (item.loading) {
    return (
      <p className="color-initial" style={{ margin: 0 }}>
        {t('Validando...')}
      </p>
    );
  }
  if (item.valida === true) {
    return (
      <p className="color-initial" style={{ margin: 0 }}>
        {item.validacion}
      </p>
    );
  } else {
    return (
      <p className="color-error" style={{ margin: 0 }}>
        {item.validacion}
      </p>
    );
  }
};

const TIPOS_VALIDAN_POR_ESTADO_VALIDA = [TipoInsumos.COSTO_HORA, TipoInsumos.REGISTRO_ACTIVIDADES];

const updateItemByIndex = (prev: LiquidacionInsumo[], index: number, patch: Partial<LiquidacionInsumo>) => {
  const next = [...prev];
  next[index] = { ...next[index], ...patch };
  return next;
};

const setItemLoading = (setItems: React.Dispatch<React.SetStateAction<LiquidacionInsumo[]>>, index: number, loading: boolean) => {
  setItems((prev) => updateItemByIndex(prev, index, { loading }));
};

interface LevelsTableProps {
  items: LiquidacionInsumo[];
  setItems: React.Dispatch<React.SetStateAction<LiquidacionInsumo[]>>;
  periodo: DateTime;
  settlementType: ISelectableOption<AgrupadorCorporacionLiquidadora>;
  system: TimeTaxSupportSystemClient;
  changeBlockUI: (state: boolean) => void;
  setButtonDisability: (can: boolean) => void;
}

export const LevelsTable: React.FC<LevelsTableProps> = ({ items, periodo, system, settlementType, setItems, changeBlockUI, setButtonDisability }) => {
  const { t } = useTranslation();

  const validateInsumos = async ({
    items,
    periodo,
    system,
    setItems,
    changeBlockUI,
  }: {
    items: LiquidacionInsumo[];
    periodo: DateTime;
    system: TimeTaxSupportSystemClient;
    setItems: React.Dispatch<React.SetStateAction<LiquidacionInsumo[]>>;
    changeBlockUI: (state: boolean) => void;
  }) => {
    const registroActividadesIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.REGISTRO_ACTIVIDADES);
    const costoHoraIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.COSTO_HORA);
    const subDiarioIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.SUB_DIARIO);

    /* ---- REGISTRO DE ACTIVIDADES ---- */
    if (registroActividadesIndex !== -1 && settlementType.data) {
      try {
        setItemLoading(setItems, registroActividadesIndex, true);

        const tipoAgrupador = settlementType.data.agrcorliq_tipo;

        const data = await system.getLiquidacionAsincronaV2System().getActividadInRange(periodo, tipoAgrupador);

        const messages: Record<AgrupadorCorporacionLiquidadoraTipo, string> = {
          [AgrupadorCorporacionLiquidadoraTipo.AJE_FILMS]: 'No hay datos de actividades en el periodo seleccionado',
          [AgrupadorCorporacionLiquidadoraTipo.ASESORIAS]: 'No hay datos de actividades en el periodo seleccionado',
        };

        const message = messages[tipoAgrupador] ?? 'No hay datos disponibles';

        setItems((prev) =>
          updateItemByIndex(prev, registroActividadesIndex, {
            valida: Array.isArray(data) && data.length > 0,
            validacion: Array.isArray(data) && data.length > 0 ? 'Insumo existente' : message,
          }),
        );
      } catch {
        setItems((prev) =>
          updateItemByIndex(prev, registroActividadesIndex, {
            valida: false,
            validacion: 'Error al validar el registro de actividades',
          }),
        );
      } finally {
        setItemLoading(setItems, registroActividadesIndex, false);
      }
    }

    /* ---------- COSTO HORA ---------- */
    if (costoHoraIndex !== -1) {
      try {
        setItemLoading(setItems, costoHoraIndex, true);

        const data = await system.getLiquidacionAsincronaV2System().getRemunInActividad(periodo, settlementType.data!.agrcorliq_tipo);

        let validacion = '';

        switch (data.status) {
          case 'NO_ACTIVITIES':
          case 'NONE_RENUMERATED':
            validacion = 'No hay datos de remuneración para ningún colaborador en el periodo seleccionado';
            break;

          case 'PARTIAL_RENUMERATED':
            validacion = 'Hay colaboradores que no tienen datos de remuneración';
            break;

          case 'ALL_RENUMERATED':
            validacion = 'Insumo existente';
            break;
        }

        setItems((prev) =>
          updateItemByIndex(prev, costoHoraIndex, {
            valida: data.status === 'ALL_RENUMERATED',
            validacion,
          }),
        );
      } catch {
        setItems((prev) =>
          updateItemByIndex(prev, costoHoraIndex, {
            valida: false,
            validacion: 'Error al validar la remuneración',
          }),
        );
      } finally {
        setItemLoading(setItems, costoHoraIndex, false);
      }
    }

    /* ---------- SUB DIARIO ---------- */
    if (subDiarioIndex !== -1) {
      setItemLoading(setItems, subDiarioIndex, true);

      const hasFile = !!items[subDiarioIndex].liqins_id;

      // Falta validar las columnas del insumo si esque existe el archivo, pero por ahora se asume que si el id del insumo existe, el archivo es correcto y se encuentra cargado

      setItems((prev) =>
        updateItemByIndex(prev, subDiarioIndex, {
          valida: hasFile,
          validacion: hasFile ? 'Insumo existente' : 'Insumo no existente',
        }),
      );
      setItemLoading(setItems, subDiarioIndex, false);
    }
  };

  React.useEffect(() => {
    validateInsumos({
      items,
      periodo,
      system,
      setItems,
      changeBlockUI,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodo, settlementType]);

  React.useEffect(() => {
    const hasLoading = items.some((i) => i.loading);
    const allValid = items.every((i) => i.valida);

    setButtonDisability(allValid && !hasLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const columns = [
    {
      key: 'liqins_id',
      fieldName: 'liqins_id',
      name: t('Estado'),
      minWidth: 50,
      maxWidth: 50,
      isResizable: false,
      onRender(item: LiquidacionInsumo) {
        return (
          <div
            style={{
              width: 50,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              className={`status-films__circle ${getStatusCircleClass(TIPOS_VALIDAN_POR_ESTADO_VALIDA.includes(item.inst_codigo) ? item.valida : !!item.liqins_id)}`}
            />
          </div>
        );
      },
    },
    {
      key: 'inst_nombre',
      fieldName: 'inst_nombre',
      name: t('Insumo'),
      minWidth: 200,
    },
    {
      key: 'valida',
      fieldName: 'valida',
      name: t('Valida'),
      minWidth: 40,
      maxWidth: 40,
      onRender: (item: LiquidacionInsumo) => {
        if (item.loading) {
          return <Spinner className="m-auto" size={SpinnerSize.xSmall} />;
        }
        return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{formatValida(item)}</div>;
      },
    },
    {
      key: 'validacion',
      fieldName: 'validacion',
      name: t('Descripción de la validacion'),
      minWidth: 400,
      maxWidth: 500,
      isResizable: true,
      onRender: (item: LiquidacionInsumo) => formatDescription(item),
    },
  ];

  return (
    <DetailsList
      className="mb-2"
      items={items}
      columns={columns}
      selectionMode={SelectionMode.none}
      checkboxVisibility={CheckboxVisibility.hidden}
      layoutMode={DetailsListLayoutMode.fixedColumns}
    />
  );
};
